// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machine, heard (M3-20 · M3-21 · M3-22).
//
// Where the three pieces meet: the mixer gives it somewhere to go, the sound bank gives it
// something to play, and the voice pool decides what loses when everything happens at once.
// What is left here is the part with opinions in it — *which* sound, *how loud*, and *where*.
//
// **How loud is not a constant.** `SimEvent.value` is the impact speed the solver measured, and
// it arrives through `PresentationEvents` for exactly this. A bumper the ball dribbles into and
// a bumper it is fired into at four metres a second are the same event with very different
// numbers, and a machine that plays them at the same level is a machine that sounds like a
// soundboard.
//
// **Where is playfield x.** 07 §8 collapses positional audio to a stereo pan on flat screens,
// and the pan is the honest one: the left slingshot is on the left. Full HRTF panning is M3-30.
//
// **The ball layer is the one part that is not event-driven** (M3-24). A rolling ball has no
// event to hang a sound on — it is a continuous state, so the bed is aimed from `World.balls`
// every frame rather than triggered. Its impacts *do* arrive as reports, but through
// `World.contactTap` rather than the event queue, because a rubber post has no switch under it
// and no rulesheet may pretend it does.
//
// Two things this deliberately does *not* do. It never touches the simulation — it reads
// stamps and drains two feeds, and holds nothing the game can see. And it never renders during
// play: every sound here is a buffer that was synthesised when the machine loaded.

import type { LoadedMachine } from '@core/machine'
import type { World } from '@core/sim/World'
import type { Ball } from '@core/sim/Ball'
import type { TiltBob } from '@core/game'
import { RING_RADIUS_X } from '@core/game'
import {
  EV_BALL_CAPTURED,
  EV_BALL_EJECTED,
  EV_BUMPER_HIT,
  EV_GATE_PASSED,
  EV_SLINGSHOT_FIRED,
  EV_SPINNER_REVOLUTION,
  EV_SWITCH_CLOSED,
  EV_TARGET_DOWN,
  EV_TARGET_UP,
} from '@core/sim/events'
import { SURFACE_COUNT } from '@core/sim/contact'
import { CaptiveBall, DropTarget, Spinner, Trigger } from '@core/sim/devices'
import { applyAudioPrefs, readAudioPrefs, type AudioPrefs } from './audioPrefs'
import { barFor, CHIME_NAMES } from './chimes'
import { IMPACT_CUES, type ImpactCue } from './impacts'
import { RollingBed, rollingBuffer, surfaceForLayer, type RollingSurface } from './rolling'
import { BANK_CLACK_SPACING } from './targets'
import {
  BOB_FULL_DB,
  bobLevel,
  PULL_FULL_DB,
  PULL_Q,
  pullCentreHz,
  pullLevel,
  renderBobLoop,
  renderPlungerLoop,
  shoveLevel,
} from './cabinet'
import { LoopVoice } from './loopVoice'
import { SpeechBank } from './speechBank'
import { MusicSequencer, type MusicState } from './sequencer'
import { MixRules, type MixState } from './mixRules'
import { nearestBar } from './tones'
import { HERE, isPlace, spotAt, spotFor, type Place, type Spot } from './space'
import { ClickClock, spinnerLevel } from './spinner'
import type { PresentationContacts } from '../engine/contactFeed'
import type { PresentationEvents } from '../engine/eventFeed'
import { Mixer, type BusName } from './mixer'
import {
  AUDIO_SEED,
  CUE_NAMES,
  CUE_SPECS,
  silentCues,
  SoundBank,
  SYNTH_CUES,
  type CueName,
} from './soundBank'
import type { SynthName } from '@core/machine'
import { TIER_VOICES, VoicePool, type VoicePriority } from './voicePool'
import type { RenderTier } from '../world/tiers'
import { Rng } from '@core/rng/Rng'
import type { NoiseSource } from './dsp'
import { noiseFrom } from './solenoids'
import {
  asHrtf,
  decibelsToGain,
  type AudioBufferLike,
  type AudioPlaybackContext,
  type BufferSourceLike,
  type GainNodeLike,
  type AudioNodeLike,
} from './webAudio'

/** How a simulation event becomes a sound. */
interface EventCue {
  readonly cue: CueName
  readonly priority: VoicePriority
  readonly bus: BusName
  /** Impact speed, m/s, at which this cue is at full level. */
  readonly referenceSpeed: number
  /** Level at full strength, dB. */
  readonly db: number
}

/**
 * The mapping, and the gaps in it are deliberate rather than forgotten.
 *
 * A spinner is a pulse train that tracks angular velocity (M3-25), a target is a clack and a
 * spring twang (M3-26), and the ball layer is its own thing entirely (M3-24). None of them is
 * a solenoid, so none of them is here: playing a coil sound for a spinner would be worse than
 * silence, because it would be *wrong* rather than missing.
 */
const EVENT_CUES: ReadonlyMap<number, EventCue> = new Map([
  [EV_BUMPER_HIT, { cue: 'popBumper', priority: 'solenoid', bus: 'mechanical', referenceSpeed: 3.5, db: -4 } as EventCue],
  [EV_SLINGSHOT_FIRED, { cue: 'slingshot', priority: 'solenoid', bus: 'mechanical', referenceSpeed: 3.5, db: -5 } as EventCue],
  // A kicker throwing a ball out. `value` is the exit speed, so a gentle feed and a hard eject
  // are already different numbers.
  [EV_BALL_EJECTED, { cue: 'scoopEject', priority: 'solenoid', bus: 'mechanical', referenceSpeed: 4, db: -3 } as EventCue],
  // A ball dropping into a lock: the trough coil is not what makes this noise, but the muffled
  // under-playfield thump is the closest voice the bank has until M3-24 gives it a real one.
  [EV_BALL_CAPTURED, { cue: 'troughKick', priority: 'ballImpact', bus: 'mechanical', referenceSpeed: 3, db: -12 } as EventCue],
])

/** Level floor, so a very soft contact is quiet rather than absent. */
const MIN_STRENGTH = 0.18

/**
 * A lane rollover, and the bar it rings.
 *
 * **Only triggers**, which is what "lane chimes" means. `EV_SWITCH_CLOSED` also comes from
 * bumpers, slingshots, kickers, ramps and drop targets — every one of which already makes its
 * own noise, so chiming on all of them would double every coil and turn a bumper storm into a
 * carillon.
 *
 * The bar rotates by device index, because that is how a real machine is wired: three chimes,
 * each on a group of switches, so the lanes answer in different voices rather than in unison.
 */
const CHIME_REFERENCE_SPEED = 2.5
const CHIME_DB = -9

/**
 * Level of each impact at full strength, dB.
 *
 * A table rather than a constant because every recipe is normalised to the same peak, and a
 * normalised peak is not a loudness: a wire's thin 180 ms ring and a wood knock's 30 ms of
 * low-frequency noise reach 0.9 with completely different energy behind them. These are the
 * numbers that make them sit together.
 *
 * The glass is the loud one on purpose. An airball into the underside of the glass is the
 * biggest surprise the machine produces, and a player who cannot hear it has no idea why the
 * ball just changed direction two feet above the playfield.
 */
const IMPACT_DB: Readonly<Record<ImpactCue, number>> = {
  impactRubber: -14,
  impactFlipperRubber: -13,
  impactMetal: -11,
  impactWood: -15,
  impactPlastic: -13,
  impactWire: -14,
  impactGlass: -8,
  impactBall: -10,
}

/** Impact speed at which an impact is at full level, m/s. */
const IMPACT_REFERENCE_SPEED = 4

/**
 * The backbox, where 07 §8 puts speech and the display.
 *
 * A constant rather than a call per callout: `spotFor` is pure and the answer never changes, and
 * a machine that says something every few seconds should not allocate a placement to say it.
 */
const BACKBOX: Spot = spotFor('backbox', HERE)

/**
 * The machine's own centre, with a pan (M9-48).
 *
 * `GameSound.PannedHere`, to the field — the shove, the plunger's twang and the tilt bob are all
 * *at the cabinet* and only *read* as left or right, so the pan is the flat collapse of a stereo
 * effect and the position is the machine. A headset that took the pan as a position would put the
 * knocker outside the cabinet.
 */
function pannedHere(pan: number): Spot {
  return { pan, db: 0, x: 0, y: 0, z: 0 }
}

/**
 * Below this the contact is real but under the machine's own noise floor, so nothing plays.
 *
 * Higher than the core's `CONTACT_REPORT_SPEED`, and the two answer different questions. The
 * core's threshold keeps the feed from overflowing; this one keeps a voice from being spent on
 * something inaudible during the six-ball frenzy when voices are the scarce thing.
 */
const IMPACT_MIN_SPEED = 0.2

/**
 * Steps before the same ball may sound the same surface again.
 *
 * Two sources of duplicates, and this handles both. The solver runs four contact passes per
 * step, and a ball wedged against two colliders can be reported by more than one of them; and
 * a ball chattering against a rail produces a burst of reports the ear hears as one contact.
 * Twenty-five milliseconds is under the shortest rattle a player can distinguish and well over
 * the four passes of a single step.
 */
const IMPACT_DEBOUNCE_STEPS = 25

/** Level of the drain roll, dB. Quiet: the machine acknowledging a loss, not announcing it. */
const DRAIN_DB = -13

/**
 * Level of one spinner click at full speed, dB.
 *
 * Quieter per click than any impact, and it has to be: a rip is forty of them a second, and a
 * spinner mixed at impact level buries the rest of the machine for two seconds every time
 * somebody makes the shot. What makes a rip enormous is the *rate*, not the level of each tick.
 */
const SPINNER_DB = -17

/**
 * The mechanisms of 07 §3.5, and how loud each is at full strength.
 *
 * A drop target going over is a real event a player is listening for, so it sits close to a
 * coil. A standup tick and a gate rattle are *information* rather than drama — the ball touched
 * something over there — and belong under the ball layer, where they read as detail rather than
 * as things happening to the player.
 */
const DROP_TARGET_DB = -8
const BANK_RESET_DB = -10
const STANDUP_DB = -13
const CAPTIVE_DB = -7
const GATE_DB = -18

/** Note buffers the sequencer holds (M3-28), and the three continuous beds. */
const MUSIC_VOICES = 7
const LOOP_BUFFERS = 3

/** The player's own half of the machine (07 §3.6), at full strength. */
const SHOVE_DB = -6
const TING_DB = -14
const PLUNGER_RELEASE_DB = -9
/** The buzzers are the loudest thing the machine does, because they are the machine saying no. */
const TILT_WARNING_DB = -6
const TILT_BUZZ_DB = -4

/**
 * The ship's AI (07 §4).
 *
 * Loud, because a callout is information and the whole point of the `speech` bus and of speech
 * being the top voice priority is that it is never the thing that gets lost. 07 §5's ducking
 * of the music under it is M3-29's job; this is the level it ducks *to*.
 */
const SPEECH_DB = -5

/**
 * A rule-fired game tone (07 §3.2).
 *
 * On the `game` bus, and forward: these are the machine *telling the player something*, and 07
 * §2's priority order puts a jackpot above every mechanical sound for exactly that reason.
 */
const TONE_DB = -8

/**
 * 07 §9's buffer cap.
 *
 * **Now a per-machine number rather than a product-wide one**, which is the thing that changed
 * when speech arrived. The cue bank is a constant — 202 buffers, the same for every machine —
 * but a machine's callouts are its own, and NEON NOVA's ten put the total at 219. A machine with
 * twenty would be over the cap, and nothing in a constant list of cues could ever notice.
 */
export const BUFFER_CAP = 220

/**
 * The synths a `sound` rule action actually plays.
 *
 * **Everything else is already sounding.** NEON NOVA's rulesheet fires `sound` forty times and
 * fifteen of those name a bumper, a slingshot, a spinner, a drop target, the captive ball, the
 * kickback or the drain — all of which the mechanical layer plays from the physics, because the
 * physical object is what makes the noise. Playing them again from the rule would sound each
 * shot twice, forty milliseconds apart, which is not an echo and not a double hit but the
 * unmistakable sound of a bug.
 *
 * The author is not wrong to have written them. "This shot sounds like a bumper" is true, and
 * saying so in the definition is how a machine that had *no* bumper would get the sound. It is
 * only redundant on this machine, which is why the count is reported rather than the action
 * being an error.
 *
 * Exported because the Machine Builder's cue tuner (M9-15) says which of an author's cues a rule
 * will actually play, and a second copy of this list in the editor is a list that can drift from
 * the one that decides.
 */
export const RULE_PLAYABLE: ReadonlySet<string> = new Set([
  'chime',
  'brassStab',
  'sweepUp',
  'sweepDown',
  'blip',
  'arpRun',
  'noiseBurst',
  'subDrop',
])

export interface MachineAudioOptions {
  readonly tier?: RenderTier
  /** Overridable so a test can play without a clock. */
  readonly now?: () => number
  /**
   * The player's own levels and mute (07 §7).
   *
   * **Applied here, at construction, rather than by whatever draws the control.** The mixer is
   * built lazily on the first user gesture, and a mute that only arrives when a HUD button mounts
   * is a mute that lets one bumper through at full volume first — on a phone, in a quiet room,
   * which is the exact moment somebody reached for it.
   *
   * Injectable so a test can state the levels rather than write to `localStorage`; read from
   * storage when it is absent, which answers with the defaults on any platform that has none.
   */
  readonly audio?: AudioPrefs
}

export class MachineAudio {
  readonly context: AudioPlaybackContext
  readonly mixer: Mixer
  readonly bank: SoundBank
  readonly voices: VoicePool

  /** Sounds started. A flat counter during play means the feed is not reaching this. */
  played = 0

  /** Every ball's rolling bed (M3-24). */
  readonly rolling: RollingBed

  /** Simulation steps to context time, for the spinner's pulse train (M3-25). */
  readonly clickClock = new ClickClock()

  /** The plunger being drawn, and the tilt bob swinging (M3-26). Both are states, not events. */
  readonly plunger: LoopVoice
  readonly bob: LoopVoice

  /** The machine's score (M3-28). Rebuilt with the machine, for the same reason speech is. */
  sequencer!: MusicSequencer

  /** 07 §7's mixing rules (M3-29). The mixer says how; this says when. */
  readonly mix: MixRules

  /**
   * `sound` effects this machine raised that the mechanical layer had already played.
   *
   * Counted rather than silently dropped: a rulesheet whose every `sound` action landed here
   * would be a machine with no game tones at all, and the count is the only way to see it.
   */
  soundsAlreadyPlayed = 0

  /** The machine's own cue table, from `audio.cues`. */
  private cues: Readonly<Record<string, Readonly<Record<string, unknown>>>> = {}

  /**
   * The gain and the placement this machine declares for each sound it makes.
   *
   * 04 §12 lets a definition give every cue a `gain` and a `spatial`, and until now the only
   * sounds that read either were the ones a *rule* fired. Everything the mechanical layer plays
   * — which is most of what a player hears — used a constant from this file and ignored what the
   * machine had said about itself. NEON NOVA declares `bumper` at 0.9 and `spatial: 'device'`,
   * and both were being thrown away.
   *
   * Keyed by cue rather than by synth so the lookup is free at play time. Where two of a
   * machine's cues name the same synth — `jackpot` and `modeShot` are both brass stabs — the
   * first wins, because the mechanical layer has no cue id to disambiguate with and a rule-fired
   * sound reads its own entry directly.
   */
  private declared = new Map<CueName, { gain: number; place: Place | undefined }>()

  /**
   * Every buffer this machine will hold once everything has warmed, against 07 §9's cap.
   *
   * Counted rather than assumed, and reported at load, because the cap is a memory budget and
   * memory is where a machine with a lot to say breaks first.
   */
  get buffers(): { total: number; cap: number; over: boolean } {
    let total = 0
    for (const cue of CUE_NAMES) total += CUE_SPECS[cue].variants
    total += this.speech.size + MUSIC_VOICES + LOOP_BUFFERS
    return { total, cap: BUFFER_CAP, over: total > BUFFER_CAP }
  }

  /**
   * The machine's callouts (M3-27). Rebuilt with the machine, because they belong to it.
   *
   * Assigned by `adoptMachine`, which the constructor calls — the assertion is about the
   * compiler not following a call, not about the field being optional.
   */
  speech!: SpeechBank

  /**
   * Cues this machine declares that have no recipe, and why.
   *
   * Reported rather than skipped in silence, the same way `loadMachine` reports a device kind it
   * has no mechanism for. A machine that names a synth nothing can render still loads and still
   * scores, and the only sign is a sound that never happens — which is indistinguishable from a
   * rule that never fires.
   */
  silent: string[] = []

  private readonly halfWidth: number
  private readonly halfLength: number

  /**
   * Where the plunger is, in the machine's own frame (M9-49, 07 §8).
   *
   * **Found here rather than passed in, which is where this differs from the native client — on
   * purpose.** `GameSound.Cabinet` takes `plungerX`/`plungerY` from its caller, so every caller has
   * to look the device up and a caller that forgets gets a twang in the wrong place with nothing
   * to say so. This layer already holds the machine and already derives `halfWidth` and
   * `halfLength` from it; the plunger is one more thing the definition knows and nobody should
   * have to be told.
   *
   * `pannedHere(1)` for a machine that states no plunger: hard right, at the cabinet, which is
   * where the sound came from before this existed and is the honest answer when there is no
   * shooter lane to put it in.
   */
  private plungerSpot: Spot = pannedHere(1)
  private eventCursor = 0
  private contactCursor = 0
  private readonly flipperPressStamp: number[] = []
  private readonly flipperReleaseStamp: number[] = []
  /** Rolling surface per simulation layer index. */
  private layerSurfaces: RollingSurface[] = []
  /** Last step each (ball, surface) pair sounded. Flat, so it costs no map lookup per contact. */
  private readonly lastImpactStep: number[] = []
  /** The step a bank was last raised on, and how many of its faces have been staggered so far. */
  private raiseStep = -1
  private raiseIndex = 0
  /** The last shove, tilt-bob contact, warning count and tilt state that were sounded. */
  private nudgeStamp = -1
  private tingStamp = -1
  private warningsHeard = 0
  private tiltHeard = false
  /** Plunger tension on the previous frame. A fall to zero is the release. */
  private tension = 0
  /** The machine is stopped and the context is suspended (M3-55). Set only by `setPaused`. */
  private paused = false
  /**
   * The pending "fade has finished, now stop the clock" timer.
   *
   * `ReturnType<typeof setTimeout>` rather than `number`, because this file is compiled against
   * both the DOM and node's types and the two disagree about what a timer handle is.
   */
  private fadeTimer: ReturnType<typeof setTimeout> | undefined
  /** The caption on screen and the context time it comes off at (NFR-022). */
  /**
   * Where the player's ears are, in the machine's own frame, or nothing (M9-48).
   *
   * `undefined` on a flat screen and in a headset that is not presenting, which is what makes
   * every voice take 07 §8's stereo collapse there. See `listenAt`.
   */
  private spatialHead:
    | { x: number; y: number; z: number; fx: number; fy: number; fz: number }
    | undefined

  private captionText = ''
  private captionUntil = 0
  /** The host's wall clock, for measuring renders. Never used to decide anything audible. */
  private readonly now: () => number

  constructor(
    context: AudioPlaybackContext,
    machine: LoadedMachine,
    options: MachineAudioOptions = {},
  ) {
    this.context = context
    this.now = options.now ?? (() => 0)
    this.mixer = new Mixer(context)
    // Before `MixRules`, and before a single voice exists. The two write different gains — the
    // player owns `volume`, the rules own `duck` — so the order is not a correctness matter, but
    // a mixer that is silent from its first sample is worth having for its own sake.
    // One read of the preferences, feeding the mixer and the captions together: two reads is
    // two chances for a player's stored choice to reach one of them and not the other.
    const prefs = options.audio ?? readAudioPrefs()
    applyAudioPrefs(this.mixer, prefs)
    this.captions = prefs.captions
    this.mix = new MixRules(this.mixer)
    this.bank = SoundBank.forContext(context, options.now)
    this.voices = new VoicePool(TIER_VOICES[options.tier ?? 'high'])
    // Playfield x runs ±half the width and a pan runs ±1. Taken from the machine's own
    // dimensions rather than from a constant, so a wider body still pans to its own edges
    // instead of running out of stereo before it runs out of playfield.
    this.halfWidth = Math.max(0.01, machine.definition.dimensions.playfield.width / 2)
    // 07 §8's distance cue comes off this. A playfield is two and a half times longer than it
    // is wide, so without it the whole top half of the table is acoustically in the player's lap.
    this.halfLength = Math.max(0.01, machine.definition.dimensions.playfield.length / 2)
    // One loop, shared by every ball. Its own seed, so adding a coil recipe does not change
    // the noise the bed is made of — which would otherwise be a silent change to the one sound
    // that is playing all the time.
    this.rolling = new RollingBed(
      context,
      rollingBuffer(context, AUDIO_SEED ^ 0x524f4c4c),
      this.mixer.bus('mechanical').input,
      this.voices,
      () => this.spatialHead,
    )
    const mechanical = this.mixer.bus('mechanical').input
    // `() => this.spatialHead`, never the value: a bed outlives the frame that built it, so one
    // handed a head would go on believing in it after the player took the headset off (M9-48).
    const head = (): { x: number; y: number; z: number } | undefined => this.spatialHead
    this.plunger = new LoopVoice(context, loopBuffer(context, renderPlungerLoop), mechanical, true, head)
    this.bob = new LoopVoice(context, loopBuffer(context, renderBobLoop), mechanical, false, head)
    this.adoptMachine(machine)
  }

  /**
   * Point the audio at a new machine.
   *
   * The harness rebuilds on `R`, which replaces the `World`. Stamps from the old one are steps
   * in a game that no longer exists, and comparing against them makes every flipper on the new
   * machine either silent or firing on its first frame.
   */
  adopt(machine: LoadedMachine): void {
    this.eventCursor = 0
    this.contactCursor = 0
    this.newGame()
    this.adoptMachine(machine)
  }

  /**
   * A new game has begun at step 0 on the same machine.
   *
   * Every memory here that is a step number — the impact debounce, the raise, the nudge and
   * tilt-bob stamps — belongs to the previous game; compared against a step counter that has
   * just gone back to 0 they would silence every impact for as long as the last game lasted.
   * The feed cursors are **not** touched: the feeds were not cleared, and a cursor past the end
   * of a cleared feed reads nothing until the feed catches up.
   */
  newGame(): void {
    this.lastImpactStep.length = 0
    this.raiseStep = -1
    this.raiseIndex = 0
    this.nudgeStamp = -1
    this.tingStamp = -1
    this.warningsHeard = 0
    this.tiltHeard = false
    this.tension = 0
    this.clickClock.reset()
    this.rolling.newGame()
    this.plunger.stop()
    this.bob.stop()
  }

  /**
   * Stop making noise while the machine is stopped, and start again when it is not (M3-55).
   *
   * **The looping voices are stopped rather than left suspended.** Suspending the context alone
   * would be enough to make a pause silent, but the rolling bed, the plunger and the tilt bob are
   * loops whose rate and filter track a ball that is no longer moving — so on resume they would
   * come back for one frame at whatever the pause froze them at, before `update()` re-aims them.
   * A one-frame blast of the wrong rolling pitch is exactly the kind of artefact a pause is
   * supposed not to have. They cost nothing to rebuild: `update()` re-establishes the bed from
   * the world's own state on the very next frame, which is the same path a fresh ball takes.
   *
   * Idempotent, because the caller is a frame loop and the honest thing for it to do is say what
   * is true every frame rather than remember what it said last time.
   */
  setPaused(paused: boolean): void {
    if (paused === this.paused) return
    this.paused = paused
    // A pause taken during a resume's fade-in, or the reverse, must not leave the old timer to
    // fire into the new state — that is how a resumed machine suspends itself a moment later.
    if (this.fadeTimer !== undefined) {
      clearTimeout(this.fadeTimer)
      this.fadeTimer = undefined
    }
    if (paused) {
      // The fade runs on the audio clock, so it has to be started while that clock is still
      // moving. Everything that makes the machine silent waits for it to finish.
      this.mixer.setPaused(true)
      this.fadeTimer = setTimeout(() => {
        this.fadeTimer = undefined
        this.rolling.stopAll()
        this.plunger.stop()
        this.bob.stop()
        // Voices holding slots across a pause would shrink the pool for as long as it lasts, and
        // their `onended` cannot fire while the clock is stopped to give them back.
        this.voices.releaseAll()
        void this.context.suspend?.().catch(() => {
          // Already suspended, or closed under a route that unmounted mid-pause. A machine that
          // fails to go quiet is not worth a line in front of the player.
        })
      }, Mixer.pauseFadeMs)
      return
    }
    // Order matters on the way back: the fade-in is scheduled against a clock that is stopped
    // until `resume` lands, so lifting the mixer first would schedule the whole move at the
    // frozen `currentTime` and the machine would come back at full volume instead of rising.
    const context = this.context
    const lift = (): void => {
      this.mixer.setPaused(false)
    }
    const resuming = context.resume?.()
    if (resuming === undefined) {
      lift()
      return
    }
    void resuming.then(lift).catch(() => {
      // A context that will not resume leaves a silent machine, which `audible` already reports.
      // The mixer is lifted anyway, so a context that recovers on its own is not left muted.
      lift()
    })
  }

  /**
   * Take the parts of a machine that outlive a single frame.
   *
   * The layer table is read from the definition rather than from the world, because the world
   * numbers layers and only the definition says what each one *is* — and "is this ball on wood,
   * on a plastic ramp or on a wire" is the whole difference between the three rolling voices.
   */
  private adoptMachine(machine: LoadedMachine): void {
    this.layerSurfaces = machine.definition.layers.map((layer) => surfaceForLayer(layer.kind))
    // The shooter lane, from the machine's own devices (M9-49). Re-read on every adopt, because a
    // different machine puts its plunger somewhere else — and the harness rebuilds on `R`.
    const plunger = machine.definition.devices.find((device) => device.kind === 'plunger')
    // `'centre' in plunger` because a device is a union and one member of it — the drop-target
    // bank — is a *rule* rather than an object and states no point. Narrowing on the field rather
    // than on the kind keeps this true if the format ever grows another.
    this.plungerSpot =
      plunger !== undefined && 'centre' in plunger
        ? spotAt(plunger.centre[0], plunger.centre[1], this.halfWidth, this.halfLength)
        : pannedHere(1)
    this.cues = machine.definition.audio?.cues ?? {}
    this.declared = readDeclarations(this.cues)
    this.soundsAlreadyPlayed = 0
    this.silent = silentCues(machine.definition.audio?.cues)
    // A new machine is a new voice and a new set of things to say, so the bank is rebuilt
    // rather than reused. Ten buffers is a fair price for not having one machine's callouts
    // available to another's rules.
    this.speech = new SpeechBank(
      this.context,
      machine.definition.audio?.speech,
      machine.definition.audio?.voice,
      this.now,
    )
    this.sequencer?.dispose()
    this.sequencer = new MusicSequencer(
      this.context,
      machine.definition.audio?.music?.program,
      this.mixer.bus('music').input,
      this.now,
    )
    this.captionText = ''
    this.captionUntil = 0
    this.adoptFlippers(machine.world)
  }

  /**
   * Say one of the machine's callouts (07 §4).
   *
   * Called from a `speech` rule effect rather than from a physics event, because a callout is
   * the *game* speaking and the simulation has no opinion about it. Centre-panned: 07 §8 puts
   * speech in the backbox, and until M3-30 gives the backbox a position, centre is where the
   * backbox is.
   */
  speak(id: string): boolean {
    const buffer = this.speech.pick(id)
    if (buffer === undefined) return false
    const played = this.playBuffer(buffer, 'speech', 'speech', 1, SPEECH_DB, 0, 0, 1, BACKBOX)
    if (!played) return false
    // The caption goes up with the sound and comes down with it. NFR-022 asks for a caption,
    // and a caption that outlasts the line it captions is a caption for the wrong line.
    this.captionText = this.speech.captionOf(id) ?? ''
    this.captionUntil = this.context.currentTime + this.speech.lengthMsOf(id) / 1000
    return true
  }

  /**
   * Whether callouts are captioned (NFR-022, `AUDIO_CAPTIONS_KEY`).
   *
   * **Gated here rather than at the renderer, so both clients behave identically.** The Quest
   * shows its caption on the dot-matrix panel and this client shows it in the HUD; a flag checked
   * in two renderers is two chances to disagree about an accessibility requirement. Held as state
   * rather than read from storage per frame — `caption` is asked on every animation frame.
   *
   * Default on: NFR-022 is met by a player who never touches the control.
   */
  captions = true

  /**
   * Is a callout being spoken right now (M8-13)?
   *
   * **The same clock the caption comes down on**, which is the whole reason it is answered here
   * rather than counted by the host: the outbound voice gate and the speech duck and the caption
   * must all agree about when a line ends, and three modules with three timers is three answers.
   */
  get speaking(): boolean {
    return this.context.currentTime < this.captionUntil
  }

  /**
   * What the machine is saying right now, or ''  (NFR-022).
   *
   * The doc for this line used to sit above `speaking`, one member up, describing the wrong getter
   * — which is harmless until somebody reads `speaking`'s contract off it and gates the duck on a
   * preference. Moved to the member it describes.
   *
   * **`speaking` is deliberately not gated by `captions`.** It drives the music duck and the
   * outbound voice gate, and a player who turned captions off did not ask the mix to stop working.
   */
  get caption(): string {
    if (!this.captions) return ''
    return this.context.currentTime < this.captionUntil ? this.captionText : ''
  }

  /**
   * The score, one frame (07 §5).
   *
   * A host call rather than something read off the world, because the layers are driven by the
   * *game* — Star Core, multiball, the running mode — and none of that is in the simulation.
   * The same reason `drain` and `cabinet` are host calls.
   */
  music(state: MusicState): void {
    this.sequencer.update(state)
  }

  /**
   * One frame of 07 §7's mixing rules.
   *
   * `speaking` is supplied from here rather than by the host, because the audio layer is the one
   * that knows: it is the same clock the caption comes down on, so the music comes back exactly
   * when the words stop.
   */
  mixdown(state: Omit<MixState, 'speaking'>): void {
    this.mix.apply(this.context.currentTime, {
      speaking: this.context.currentTime < this.captionUntil,
      multiball: state.multiball,
      tilted: state.tilted,
    })
  }

  /**
   * A click that is not the game's: the bench's snap rule catching the part in hand (M13-13).
   *
   * The standup tick — the shortest, driest click in the bank, and the cue the native client
   * plays for a button press — at the point on the playfield the part is over, on the
   * mechanical bus so the author's own mix rules apply to it. Nothing declared in a machine's
   * cue table changes it: it is the bench's sound, not the machine's.
   */
  click(x: number, y: number): boolean {
    return this.playAt('standupTick', 'ballImpact', 'mechanical', 0.6, STANDUP_DB, spotAt(x, y, this.halfWidth, this.halfLength))
  }

  /**
   * A `sound` rule action (07 §3.2).
   *
   * Resolves the machine's own cue table rather than a cue name of ours: 04 §12 lets a
   * definition name its sounds and give each one a synth, a pitch and a gain, and this is the
   * only place that promise is kept.
   *
   * Returns false for a cue the mechanical layer has already played, which is most of them —
   * see `RULE_PLAYABLE`.
   */
  sound(id: string): boolean {
    const cue = this.cues[id]
    if (cue === undefined) return false
    const synth = cue['synth']
    if (typeof synth !== 'string') return false
    if (!RULE_PLAYABLE.has(synth)) {
      this.soundsAlreadyPlayed++
      return false
    }

    const gain = typeof cue['gain'] === 'number' ? cue['gain'] : 1
    const pitch = typeof cue['pitch'] === 'number' ? cue['pitch'] : 1

    if (synth === 'chime') return this.playChime(cue['notes'], gain, pitch)

    const buffer = this.bank.pick(synth as CueName)
    if (buffer === undefined) return false
    // 07 §7's jackpot emphasis: the music steps back 3 dB for 300 ms under a brass stab. Keyed
    // on the *synth* rather than on a cue id, so any machine's jackpot gets it.
    if (synth === 'brassStab') this.mix.emphasise(this.context.currentTime)
    return this.playBuffer(buffer, 'jackpot', 'game', gain, TONE_DB, 0, 0, pitch, HERE)
  }

  /**
   * A chime cue, at the notes the definition asked for.
   *
   * 04 lets a `chime` cue carry a `notes` array — NEON NOVA's lane chime asks for 880 and 1320
   * — and the bank holds three bars at fixed pitches. Nothing read that array until now. The
   * nearest bar is played at the rate that puts it on the note, which is not an approximation:
   * a bar is a set of inharmonic partials in fixed ratio, so transposing one *is* the same bar
   * cut to a different length, and that is what a real chime bank is.
   */
  private playChime(notes: unknown, gain: number, pitch: number): boolean {
    const bars = CHIME_NAMES.map((name) => barFor(name))
    const wanted = Array.isArray(notes) ? notes.filter((n): n is number => typeof n === 'number') : []
    if (wanted.length === 0) {
      const buffer = this.bank.pick(CHIME_NAMES[1] ?? 'chimeMid')
      return buffer !== undefined && this.playBuffer(buffer, 'jackpot', 'game', gain, TONE_DB, 0, 0, pitch, HERE)
    }
    let played = false
    for (const note of wanted) {
      const { bar, rate } = nearestBar(note * pitch, bars)
      const buffer = this.bank.pick(CHIME_NAMES[bar] ?? 'chimeMid')
      if (buffer === undefined) continue
      if (this.playBuffer(buffer, 'jackpot', 'game', gain, TONE_DB, 0, 0, rate, HERE)) played = true
    }
    return played
  }

  /**
   * One frame. Drains the events the simulation recorded and looks at the coils.
   *
   * Called from the render loop rather than from the step callback: sixteen steps of events
   * arrive together and are played together, which is right — they happened within sixteen
   * milliseconds of each other, and no ear resolves that.
   */
  update(
    world: World,
    feed: PresentationEvents | undefined,
    contacts?: PresentationContacts,
  ): void {
    // One deferred render per frame (M3-21) — a cue while any are pending, then a note, then a
    // callout. Cues first because they are what the opening seconds are made of; the score's
    // seven notes next, because the bass starts the moment a ball is in play; the callouts
    // last, because the earliest a rule can ask for one is several seconds in. One render per
    // frame rather than one of each, because the point is a bounded cost, not a fast queue.
    if (this.bank.pending === 0 && this.sequencer.warmOne() === undefined) this.speech.warmOne()

    // One deferred cue per frame (M3-21). The cues that wait on something the player has not
    // done yet are left out of the load render so the machine is playable sooner, and this is
    // what fills them in — a couple of milliseconds at most, inside a sixteen-millisecond frame,
    // for the fifth of a second it takes to work through the list. Anything wanted before the
    // warm-up reaches it is rendered by `pick` on the spot instead.
    this.bank.warmOne()

    if (feed !== undefined) {
      this.eventCursor = feed.read(this.eventCursor, (type, device, ball, step, value) => {
        if (type === EV_SWITCH_CLOSED) {
          this.switchClosed(world, device, ball, value)
          return
        }
        if (type === EV_TARGET_DOWN) {
          this.targetMoved(world, device, step, false)
          return
        }
        if (type === EV_TARGET_UP) {
          this.targetMoved(world, device, step, true)
          return
        }
        if (type === EV_GATE_PASSED) {
          this.playAt(
            'gateRattle',
            'ballImpact',
            'mechanical',
            impactStrength(value),
            GATE_DB,
            this.spotOfDevice(world, device),
          )
          return
        }
        if (type === EV_SPINNER_REVOLUTION) {
          this.clickFor(world, device, step)
          return
        }
        const cue = EVENT_CUES.get(type)
        if (cue === undefined) return
        this.playAt(
          cue.cue,
          cue.priority,
          cue.bus,
          strength(value, cue.referenceSpeed),
          cue.db,
          this.spotOfBall(world, ball),
        )
      })
    }
    if (contacts !== undefined) {
      this.contactCursor = contacts.read(this.contactCursor, (ball, surface, speed, step) => {
        this.playImpact(world, ball, surface, speed, step)
      })
    }
    this.pollFlippers(world)
    this.pollNudge(world)
    this.updateRolling(world)
  }

  /**
   * The cabinet: the plunger the player is drawing and the bob they set swinging (07 §3.6).
   *
   * Called by the host rather than driven from the world, because neither is a physical event
   * the solver produces. The plunger's tension is an input the host is holding, and the tilt bob
   * belongs to `Game` — which is also what makes a drain a host call.
   *
   * `tension` is 0…1 and must already be zero when there is nothing to plunge: the *fall* to
   * zero is what fires the release, so a host that leaves it high after the ball has gone gets a
   * twang with no plunger behind it.
   */
  cabinet(bob: TiltBob | undefined, tension: number): void {
    const level = pullLevel(tension)
    this.plunger.aim({
      level: level * decibelsToGain(PULL_FULL_DB),
      pan: 1,
      centreHz: pullCentreHz(tension),
      q: PULL_Q,
      // **At the shooter lane** (M9-49), which is a real point on the playfield rather than the
      // hard-right pan this used to carry. The pan above stays 1: it is the flat collapse, and a
      // plunger *is* on the player's right whatever the machine's geometry says.
      at: this.plungerSpot,
    })
    if (level <= 0 && this.tension > 0) {
      // The rod let go. Its own tension sets the level, so a half-pull twangs half as hard.
      this.playCue('plungerRelease', 'solenoid', 'mechanical', this.tension, PLUNGER_RELEASE_DB, 1, 0, this.plungerSpot)
    }
    this.tension = level > 0 ? tension : 0

    if (bob === undefined) {
      this.bob.stop()
      return
    }

    // The stereo arc of 07 §3.6: the ring is audible in the ear the bob is swinging towards.
    const ting = clampPan(bob.x / RING_RADIUS_X)
    this.bob.aim({
      level: bobLevel(bob.proximity) * decibelsToGain(BOB_FULL_DB),
      pan: ting,
      // The bob hangs under the playfield in the middle of the cabinet, which is where the
      // machine's own origin is — so its *place* does not swing even though its pan does.
      at: pannedHere(ting),
    })

    if (bob.contactedAtStep !== this.tingStamp) {
      this.tingStamp = bob.contactedAtStep
      if (bob.contactedAtStep >= 0) {
        this.playCue('tiltBob', 'knocker', 'mechanical', 1, TING_DB, ting, 0, pannedHere(ting))
      }
    }

    // The buzzers are derived rather than called, so a host cannot forget one. `warnings` counts
    // up and `tilted` latches, and both are exactly what the player is being told.
    if (bob.warnings > this.warningsHeard) {
      this.warningsHeard = bob.warnings
      this.playCue('tiltWarning', 'knocker', 'game', 1, TILT_WARNING_DB, 0, 0, HERE)
    }
    const dead = bob.tilted || bob.slammed
    if (dead && !this.tiltHeard) {
      this.playCue('tiltBuzz', 'knocker', 'game', 1, TILT_BUZZ_DB, 0, 0, HERE)
    }
    if (!dead) this.warningsHeard = Math.min(this.warningsHeard, bob.warnings)
    this.tiltHeard = dead
  }

  /**
   * A shove, from the stamp `World.nudge` leaves.
   *
   * Not polled from `frameVX`, which is the honest-looking mistake: the frame spring rings for
   * about a third of a second after a nudge, so a layer watching the velocity cannot tell a
   * fresh shove from the tail of the last one and thuds once a frame while the cabinet settles.
   */
  private pollNudge(world: World): void {
    if (world.nudgedAtStep === this.nudgeStamp) return
    this.nudgeStamp = world.nudgedAtStep
    if (world.nudgedAtStep < 0) return
    const level = shoveLevel(world.nudgeImpulseX, world.nudgeImpulseY)
    if (level <= 0) return
    // Panned by the direction of the shove: a slap on the left side is heard on the left.
    const pan = clampPan(world.nudgeImpulseX / SHOVE_REFERENCE_PAN)
    this.playCue('cabinetShove', 'solenoid', 'mechanical', level, SHOVE_DB, pan, 0, pannedHere(pan))
  }

  /**
   * One ball striking one surface.
   *
   * The surface code is the core's material index, and `IMPACT_CUES` is in the same order — a
   * test asserts that, because getting it wrong gives a ball that sounds like wood when it hits
   * a rail, which is plausible enough that nobody would ever file it as a bug.
   */
  private playImpact(
    world: World,
    ball: number,
    surface: number,
    speed: number,
    step: number,
  ): void {
    if (speed < IMPACT_MIN_SPEED) return
    const cue = IMPACT_CUES[surface]
    if (cue === undefined) return

    // Strided by the number of surface codes rather than by the cue count, because the key
    // space is the solver's, not the bank's: a surface the bank has no recipe for has already
    // returned above, and a stride that shrank with the cue list would collide two balls.
    const key = ball * SURFACE_COUNT + surface
    const last = this.lastImpactStep[key]
    if (last !== undefined && step - last < IMPACT_DEBOUNCE_STEPS) return
    this.lastImpactStep[key] = step

    this.playAt(
      cue,
      'ballImpact',
      'mechanical',
      impactStrength(speed),
      IMPACT_DB[cue],
      this.spotOfBall(world, ball),
    )
  }

  /**
   * The rolling bed, aimed once a frame.
   *
   * Not event-driven and it cannot be: rolling is a state, and the only honest source for it is
   * the ball itself. `touchingSurface` is the solver's own answer to "did this ball touch
   * anything to roll on last step", which is exactly the question — an airball is silent, and a
   * ball cradled on a bat is below the gate.
   *
   * The wire case is the exception. A ball riding a habitrail is `heldBy` the rail and is not
   * run through the contact solver at all, so it never sets `touchingSurface` — and a habitrail
   * is one of the most recognisable rolling sounds a machine makes. Its layer says what it is.
   */
  private updateRolling(world: World): void {
    for (const ball of world.balls) {
      if (!ball.active) {
        this.rolling.silence(ball.id)
        continue
      }
      const surface = this.layerSurfaces[ball.layer] ?? 'wood'
      // The bed takes a pan and sets its own level from the ball's speed, so the distance
      // term is applied to the level it computes rather than through `playAt`.
      const at = spotAt(ball.x, ball.y, this.halfWidth, this.halfLength)
      this.rolling.update(
        ball.id,
        ball.speed(),
        surface,
        at,
        world.stepIndex,
        rollingOn(ball, surface),
      )
    }
  }

  /**
   * A ball leaving the playfield (07 §3.3).
   *
   * Called by the host rather than driven from a feed, because a drain is not a physical event:
   * the simulation has no opinion about it, and `Game` is what decides a ball below the apron
   * is gone. Passing it in keeps that decision in the one place a replay validator also makes
   * it.
   *
   * Only the roll. The trough kick 07 puts after it is a real coil firing when the machine
   * serves the next ball, and it already plays from `EV_BALL_EJECTED` — synthesising a second
   * one here would double it.
   */
  drain(x: number): boolean {
    // At the apron, which is the near end of the playfield — the closest a sound gets, and the
    // reason a drain is as loud as it is dispiriting.
    return this.playAt(
      'drainRoll',
      'ballImpact',
      'mechanical',
      1,
      DRAIN_DB,
      spotAt(x, -this.halfLength, this.halfWidth, this.halfLength),
    )
  }

  /**
   * A switch closing, which is not by itself a sound.
   *
   * `EV_SWITCH_CLOSED` comes from lane rollovers, bumpers, slingshots, kickers, ramps, drop
   * targets, standups and the captive ball. Most of those already make a noise of their own —
   * a bumper thumps, a target clacks — so this asks the world what the device actually *is*
   * rather than trusting the event type, and stays silent for anything already covered.
   *
   * Three of them are only audible here. A **lane** rings a chime. A **standup** cannot drop and
   * so emits nothing but this, meaning the switch closure is the entire mechanism; the metallic
   * part of 07 §3.5's "metallic tick with a faint switch-blade buzz" comes from the contact
   * layer's own plastic or metal impact, and the buzz comes from here. The **captive ball** is
   * struck through a rubber and needs its own two-tone click, because the ball layer's `impactBall`
   * would tell the player they had a second ball loose, which is a lie about the state of the game.
   */
  private switchClosed(world: World, device: number, ball: number, value: number): void {
    if (device < 0) return
    const it = world.devices[device]

    if (it instanceof Trigger) {
      const bar = CHIME_NAMES[device % CHIME_NAMES.length]!
      this.playAt(
        bar,
        'solenoid',
        'game',
        strength(value, CHIME_REFERENCE_SPEED),
        CHIME_DB,
        this.spotOfBall(world, ball),
      )
      return
    }

    if (it instanceof DropTarget && !it.canDrop) {
      this.playAt(
        'standupTick',
        'ballImpact',
        'mechanical',
        impactStrength(value),
        STANDUP_DB,
        this.spotOfDevice(world, device),
      )
      return
    }

    if (it instanceof CaptiveBall) {
      // The captive ball reports the approach as a negative number, because it is measuring a
      // closing speed rather than a separation. `impactStrength` takes the magnitude.
      this.playAt(
        'captiveClick',
        'ballImpact',
        'mechanical',
        impactStrength(value),
        CAPTIVE_DB,
        this.spotOfDevice(world, device),
      )
    }
  }

  /**
   * A drop target going over, or a bank of them coming back up.
   *
   * One cue for both directions, because it is one mechanism — a plastic face hitting a stop
   * with a spring behind it. What differs is what happens around it, and the reset is where 07
   * §3.5 is emphatic: "the reset solenoid, then three clacks 18 ms apart — **genuinely three,
   * not one**."
   *
   * Every target in a bank is raised on the same simulation step, so all of them arrive here
   * together and would land on one instant. They are staggered by `BANK_CLACK_SPACING`, and the
   * coil that drove them fires once, ahead of the first. A real bank's faces do not pop up in
   * unison: one coil drives a common bar and each face arrives as fast as its own spring allows.
   */
  private targetMoved(world: World, device: number, step: number, up: boolean): void {
    if (!(world.devices[device] instanceof DropTarget)) return
    const at = this.spotOfDevice(world, device)

    if (!up) {
      this.playAt('dropTargetClack', 'solenoid', 'mechanical', 1, DROP_TARGET_DB, at)
      return
    }

    // Targets raised on the same step belong to one reset. The first also fires the coil.
    const now = this.context.currentTime
    if (step !== this.raiseStep) {
      this.raiseStep = step
      this.raiseIndex = 0
      this.playAt('bankReset', 'solenoid', 'mechanical', 1, BANK_RESET_DB, at)
    }
    this.raiseIndex++
    this.playAt(
      'dropTargetClack',
      'solenoid',
      'mechanical',
      1,
      DROP_TARGET_DB,
      at,
      now + this.raiseIndex * BANK_CLACK_SPACING,
    )
  }

  /**
   * One revolution of a spinner, placed on the clock rather than on the frame.
   *
   * `value` is the signed angular velocity the vane was turning at — which it was not until
   * M3-25, because the field carried the direction alone and a magnitude is exactly what
   * `SimEvent.value` is documented to be. The sign is thrown away here: a vane clicks the same
   * whichever way it is going round.
   *
   * The start time comes from the event's own step through a clock anchored once rather than
   * per frame, so the intervals are the simulation's own however the frames fell. See the notes
   * on `ClickClock` and `SPINNER_LATENCY` for why this one cue is worth the delay and why
   * deriving the time from each frame's own `now` was not enough.
   */
  private clickFor(world: World, device: number, step: number): void {
    const spinner = world.devices[device]
    if (!(spinner instanceof Spinner)) return
    const level = spinnerLevel(spinner.omega)
    if (level <= 0) return
    this.playAt(
      'spinnerClick',
      'ballImpact',
      'mechanical',
      level,
      SPINNER_DB,
      this.spotOfDevice(world, device),
      this.clickClock.timeFor(step, this.context.currentTime),
    )
  }

  /**
   * Start one sound.
   *
   * Returns false when the pool refused, which is a real answer rather than a failure: during a
   * six-ball frenzy something has to lose, and the pool has already decided it was this.
   */
  /**
   * Play a cue *somewhere* (M3-30, 07 §8).
   *
   * The one place a sound's position becomes a pan and a level, and the one place the machine's
   * own `gain` and `spatial` are applied. Everything positional goes through here; `playCue`
   * stays as it was for the handful of sounds that are nowhere in particular.
   */
  private playAt(
    cue: CueName,
    priority: VoicePriority,
    bus: BusName,
    strengthOf: number,
    db: number,
    at: Spot,
    when = 0,
  ): boolean {
    const declared = this.declared.get(cue)
    const spot = spotFor(declared?.place, at)
    return this.playCue(
      cue,
      priority,
      bus,
      strengthOf * (declared?.gain ?? 1),
      db + spot.db,
      spot.pan,
      when,
      spot,
    )
  }

  /** Where a ball is, as a placement. Centre and near when there is no ball to ask. */
  private spotOfBall(world: World, ball: number): Spot {
    const b = ball >= 0 ? world.balls[ball] : undefined
    return b === undefined ? HERE : spotAt(b.x, b.y, this.halfWidth, this.halfLength)
  }

  /** Where a device is, as a placement. */
  private spotOfDevice(world: World, device: number): Spot {
    const d = device >= 0 ? world.devices[device] : undefined
    return d === undefined ? HERE : spotAt(d.originX, d.originY, this.halfWidth, this.halfLength)
  }

  playCue(
    cue: CueName,
    priority: VoicePriority,
    bus: BusName,
    strengthOf: number,
    db: number,
    pan: number,
    /**
     * Context time to start at, or 0 for "now".
     *
     * Only the spinner passes this. Everything else is a response to something the player just
     * did or just saw, and a scheduled sound is a delayed one — see `SPINNER_LATENCY`.
     */
    when = 0,
    /**
     * Where the sound is, in the machine's own frame (M9-48).
     *
     * `playSpatial` has always computed this and then thrown everything but the pan and the level
     * away, one call short of the voice that would have used it. Absent means *not placed* — see
     * `playBuffer`.
     */
    at?: Spot,
  ): boolean {
    const buffer = this.bank.pick(cue)
    if (buffer === undefined) return false
    return this.playBuffer(buffer, priority, bus, strengthOf, db, pan, when, 1, at)
  }

  /**
   * Where the player's ears are, in the machine's own frame (M9-48).
   *
   * **The listener moves, not the machine**, which is the whole reason this is affordable: a
   * cabinet has a few dozen emitters and a player has one head, so posing the head into
   * machine-local coordinates once a frame costs one transform where posing every voice into the
   * room would cost one each. It also means a voice's position never has to be rewritten when the
   * player picks the machine up and carries it across the room.
   *
   * Set by `machineRuntime` from the XR frame; `undefined` means there is no head — a flat screen,
   * or a headset that is not presenting — and every voice then takes the stereo collapse, which is
   * exactly what 07 §8's last line asks for.
   */
  listenAt(head: { x: number; y: number; z: number; fx: number; fy: number; fz: number } | undefined): void {
    this.spatialHead = head
    const listener = this.context.listener
    if (listener === undefined || head === undefined) return
    // Web Audio is x right, y up, −z forward. The machine's frame is x right, y *up the
    // playfield*, z above its surface. One conversion, at the one place that knows both.
    listener.positionX.value = head.x
    listener.positionY.value = head.z
    listener.positionZ.value = -head.y
    listener.forwardX.value = head.fx
    listener.forwardY.value = head.fz
    listener.forwardZ.value = -head.fy
    // Up is the playfield's own normal, which for a head that is upright is close enough to the
    // room's up that the difference is not audible — and is the vector the emitters are placed
    // against, so the two cannot disagree about which way the backbox is.
    listener.upX.value = 0
    listener.upY.value = 1
    listener.upZ.value = 0
  }

  /**
   * A panner for one voice: HRTF where there is a head, the stereo collapse where there is not.
   *
   * **`StereoPannerLike`'s own comment promised this and nobody built it** — *"a `PannerNode` with
   * HRTF is M3-30 and is what the headset gets"* — and `space.ts`'s header said the same thing
   * about the same seam. Four milestones later every sound in the browser still went through a
   * `StereoPannerNode`, so in WebXR the knocker in the cabinet head, the speech from the backbox
   * and a bumper at the far end of the playfield were the same distance away at the same height.
   *
   * **Three conditions, all of them necessary.** A head, a context that can build a panner, and a
   * position for this voice. A stub context has no `createPanner`; a flat screen has no head; a
   * cue with no `spatial` and no device has no point. Any of the three missing is a voice that
   * takes the pan, which is the behaviour every one of them had before this existed.
   *
   * The rolloff itself is `asHrtf`'s, shared with the continuous beds: a rolling ball whose
   * distance model disagreed with the bumper it just hit would be the same machine heard two ways.
   */
  private placeVoice(pan: number, at: Spot | undefined): AudioNodeLike {
    const head = this.spatialHead
    const create = this.context.createPanner
    if (head !== undefined && create !== undefined && at !== undefined) {
      const panner = asHrtf(create.call(this.context))
      panner.positionX.value = at.x
      panner.positionY.value = at.z
      panner.positionZ.value = -at.y
      return panner
    }
    const stereo = this.context.createStereoPanner()
    stereo.pan.value = Math.min(Math.max(pan, -1), 1)
    return stereo
  }

  /**
   * Play a buffer.
   *
   * Split out of `playCue` when speech arrived. A callout comes from the machine's own bank
   * rather than from `CUE_SPECS`, and everything after the lookup — the pool slot, the gain,
   * the panner, the teardown that must not run twice — is identical and is the part with the
   * defects in it.
   */
  private playBuffer(
    buffer: AudioBufferLike,
    priority: VoicePriority,
    bus: BusName,
    strengthOf: number,
    db: number,
    pan: number,
    when: number,
    rate = 1,
    /**
     * Where the sound is, in the machine's own frame (M9-48).
     *
     * **Absent means the caller has no machine frame at all**, not "at the player": every sound
     * that belongs to a machine passes one, `HERE` included, because the native client does —
     * `GameSound` hands `SoundSpace.Here` to the tilt buzz and the chimes and `PannedHere(pan)`
     * to the shove, all of them at the *machine's* origin with `spatialBlend = 1`. A browser that
     * read the same absence as "inside the player's head" would be the same machine heard two
     * ways, which is exactly the divergence 07 §9's parity fixture exists to prevent.
     */
    at?: Spot,
  ): boolean {
    let source: BufferSourceLike | undefined
    let gain: GainNodeLike | undefined
    let panner: AudioNodeLike | undefined

    const teardown = (): void => {
      // `onended` is cleared first: `stop()` fires it, and a teardown that re-entered here
      // would release the pool slot twice — the second release landing on whatever voice had
      // taken it in between.
      if (source !== undefined) {
        source.onended = null
        source.stop()
        source.disconnect()
      }
      gain?.disconnect()
      panner?.disconnect()
    }

    const handle = this.voices.acquire(priority, teardown)
    if (handle === undefined) return false

    source = this.context.createBufferSource()
    source.buffer = buffer
    if (rate !== 1) source.playbackRate.value = rate
    gain = this.context.createGain()
    gain.gain.value = decibelsToGain(db) * strengthOf
    // **Where the sound is, when there is a head to hear it from** (M9-48, 07 §8). `placeVoice`
    // answers with an HRTF panner in a headset and the stereo collapse everywhere else — the same
    // `Spot` drives both, which is what `space.ts` has said since M3-30 and what nothing built.
    panner = this.placeVoice(pan, at)

    source.connect(gain)
    gain.connect(panner)
    panner.connect(this.mixer.bus(bus).input)

    source.onended = () => {
      // Release before tearing down, so the slot is free the instant the sound is over rather
      // than one frame later — a one-shot that holds its slot through its own cleanup is a
      // pool that is permanently a few voices smaller than it says it is.
      this.voices.release(handle)
      teardown()
    }
    source.start(when)
    this.played++
    return true
  }

  dispose(): void {
    // A machine torn down mid-fade would otherwise suspend a context the runtime is about to
    // close, and touch a mixer that no longer has nodes.
    if (this.fadeTimer !== undefined) {
      clearTimeout(this.fadeTimer)
      this.fadeTimer = undefined
    }
    this.rolling.stopAll()
    this.plunger.stop()
    this.bob.stop()
    this.sequencer.dispose()
    this.voices.releaseAll()
    this.mixer.dispose()
  }

  private adoptFlippers(world: World): void {
    this.flipperPressStamp.length = 0
    this.flipperReleaseStamp.length = 0
    for (const flipper of world.flippers) {
      this.flipperPressStamp.push(flipper.pressedAtStep)
      this.flipperReleaseStamp.push(flipper.releasedAtStep)
    }
  }

  /**
   * The flippers, from their own coil stamps.
   *
   * Not from the event queue, because a flipper coil emits no event — it is driven by a button
   * rather than by a ball. Not from `pressed` either: a tap shorter than a frame is invisible
   * to a poll of the current state, and the flipper is the sound a player hears most, so the
   * one that goes missing is the one they notice.
   */
  private pollFlippers(world: World): void {
    for (let i = 0; i < world.flippers.length; i++) {
      const flipper = world.flippers[i]!
      // At the bat's own pivot. Upper flippers exist and are further up the table, so the
      // distance term is not a formality even on a machine whose main pair is at the bottom.
      const at = spotAt(flipper.pivotX, flipper.pivotY, this.halfWidth, this.halfLength)

      if (flipper.pressedAtStep !== this.flipperPressStamp[i]) {
        this.flipperPressStamp[i] = flipper.pressedAtStep
        if (flipper.pressedAtStep >= 0) {
          this.playAt('flipper', 'solenoid', 'mechanical', 1, -7, at)
        }
      }
      if (flipper.releasedAtStep !== this.flipperReleaseStamp[i]) {
        this.flipperReleaseStamp[i] = flipper.releasedAtStep
        if (flipper.releasedAtStep >= 0) {
          this.playAt('flipperReturn', 'solenoid', 'mechanical', 1, -13, at)
        }
      }
    }
  }
}

/**
 * Impact speed to a 0…1 level with a soft knee (07 §3.3).
 *
 * The knee is the difference between an impact layer that works and one that is a wall of
 * clicks. Linear, a 0.4 m/s tap against a rail plays at a tenth of level and vanishes under
 * the music, while every hard hit sits at the top — so the layer is either inaudible or
 * uniform. The 0.6 exponent lifts the quiet end without flattening the loud one, which is what
 * "soft knee" means once it has to be a number.
 */
export function impactStrength(speed: number): number {
  const raw = Math.min(Math.abs(speed) / IMPACT_REFERENCE_SPEED, 1)
  return Math.pow(raw, 0.6)
}

/**
 * Did this ball have something under it on the last step the frame saw?
 *
 * Not the same question as "is it rolling" — `RollingBed` holds this for ninety steps, because
 * a frame samples one step in sixteen and a rolling ball chatters. See `ROLL_HOLD_STEPS`.
 *
 * A wire habitrail moves the ball itself and never runs it through the contact solver, so
 * `touchingSurface` is stale for it and its layer is the authority instead.
 */
function rollingOn(ball: Ball, surface: RollingSurface): boolean {
  return surface === 'wire' ? true : ball.touchingSurface && ball.heldBy < 0
}

/** Nudge impulse that pans a shove fully to one side, N·s. A firm sideways slap. */
const SHOVE_REFERENCE_PAN = 20

/** Render a loop and upload it. Its own seed, so adding a recipe never changes an existing bed. */
function loopBuffer(
  context: AudioPlaybackContext,
  render: (sampleRate: number, noise: NoiseSource) => Float32Array,
): AudioBufferLike {
  const samples = render(context.sampleRate, noiseFrom(new Rng(AUDIO_SEED ^ 0x50554c4c)))
  const buffer = context.createBuffer(1, samples.length, context.sampleRate)
  buffer.getChannelData(0).set(samples)
  return buffer
}

/** Impact speed to a 0…1 level, with a floor so a soft contact is quiet rather than silent. */
export function strength(value: number, referenceSpeed: number): number {
  const raw = Math.min(Math.abs(value) / Math.max(referenceSpeed, 0.01), 1)
  return MIN_STRENGTH + (1 - MIN_STRENGTH) * raw
}

function clampPan(pan: number): number {
  return Number.isFinite(pan) ? Math.min(Math.max(pan, -1), 1) : 0
}

/** What a machine says about its own sounds, indexed by the cue each one resolves to. */
function readDeclarations(
  cues: Readonly<Record<string, Readonly<Record<string, unknown>>>>,
): Map<CueName, { gain: number; place: Place | undefined }> {
  const out = new Map<CueName, { gain: number; place: Place | undefined }>()
  for (const id of Object.keys(cues).sort()) {
    const entry = cues[id]
    if (entry === undefined) continue
    const synth = entry['synth']
    if (typeof synth !== 'string') continue
    const resolved = SYNTH_CUES[synth as SynthName] as readonly CueName[] | undefined
    if (resolved === undefined) continue
    const gain = typeof entry['gain'] === 'number' ? entry['gain'] : 1
    const place = isPlace(entry['spatial']) ? entry['spatial'] : undefined
    for (const cue of resolved) {
      if (!out.has(cue)) out.set(cue, { gain, place })
    }
  }
  return out
}

